import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AssistantCommandView,
  AssistantContext,
  ConfirmCommandResponse,
  ConversationDetail,
  ConversationSummary,
  SendMessageResponse,
} from "@opus-dt/shared-types";
import { api } from "@/lib/api-client";
import { useAssistantDock } from "./store";
import { effectKeys } from "./command-effect-keys";

// Hooks do assistente. Mutações invalidam explicitamente (tabela viva do CLAUDE.md).
// O SSE (useAssistantStream) é complemento: reconcilia no terminal.

const statusKey = ["assistant", "status"] as const;
const conversationsKey = ["assistant", "conversations"] as const;
const conversationKey = (id: string) => ["assistant", "conversation", id] as const;

export function useAssistantStatus() {
  return useQuery({
    queryKey: statusKey,
    queryFn: () => api.get<{ enabled: boolean }>("/api/v1/assistant/status"),
    staleTime: 5 * 60_000,
  });
}

export function useConversations(enabled = true) {
  return useQuery({
    queryKey: conversationsKey,
    queryFn: () => api.get<ConversationSummary[]>("/api/v1/assistant/conversations"),
    enabled,
  });
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: conversationKey(id ?? "none"),
    queryFn: () => api.get<ConversationDetail>(`/api/v1/assistant/conversations/${id!}`),
    enabled: !!id,
  });
}

// Envia a mensagem — cria a conversa LAZY (POST /conversations) quando ainda não há uma ativa,
// gravando o id no store. 202 ⇒ o dock abre o stream do turno retornado.
export function useSendMessage() {
  const qc = useQueryClient();
  const activeId = useAssistantDock((s) => s.activeConversationId);
  const setActive = useAssistantDock((s) => s.setActiveConversation);
  return useMutation({
    mutationFn: async (input: {
      content: string;
      context?: AssistantContext;
    }): Promise<SendMessageResponse> => {
      let conversationId = activeId;
      if (!conversationId) {
        const conv = await api.post<ConversationSummary>("/api/v1/assistant/conversations", {});
        conversationId = conv.id;
        setActive(conversationId);
      }
      return api.post<SendMessageResponse>(
        `/api/v1/assistant/conversations/${conversationId}/messages`,
        { content: input.content, context: input.context }
      );
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: conversationKey(res.conversationId) });
      void qc.invalidateQueries({ queryKey: conversationsKey });
    },
  });
}

export function useCancelTurn(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (turnId: string) =>
      api.post<{ id: string; status: string }>(`/api/v1/assistant/turns/${turnId}/cancel`, {}),
    onSuccess: () => {
      if (conversationId) void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
  });
}

export interface ConfirmCommandInput {
  command: AssistantCommandView;
  confirmationName?: string; // destrutivo: nome digitado p/ confirmar
}

// Confirm/reject de comandos ACTION. skipErrorToast: o ActionCard trata error.code localmente
// (expirado/conflito/nome divergente) — o toast global cru enganaria.
export function useConfirmCommand(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ command, confirmationName }: ConfirmCommandInput) =>
      api.post<ConfirmCommandResponse>(`/api/v1/assistant/commands/${command.id}/confirm`, {
        ...(confirmationName != null ? { confirmationName } : {}),
      }),
    onSuccess: (_res, { command }) => {
      if (conversationId) void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
      for (const key of effectKeys(command)) void qc.invalidateQueries({ queryKey: key });
    },
    // Falha (ex.: dispatch rejeitado por domínio ⇒ o servidor marca o comando como FAILED, ou
    // mismatch/expirado): rebusca a conversa p/ o card refletir o estado real. Sem isso o card fica
    // em PROPOSED obsoleto e o "Dispensar" seguinte estoura ASSISTANT_COMMAND_INVALID_STATE silencioso.
    onError: () => {
      if (conversationId) void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
    meta: { skipErrorToast: true },
  });
}

export function useRejectCommand(conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commandId: string) =>
      api.post<{ id: string; status: string }>(
        `/api/v1/assistant/commands/${commandId}/reject`,
        {}
      ),
    onSuccess: () => {
      if (conversationId) void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
    // Reject sobre um comando que já saiu de PROPOSED (FAILED/EXECUTED/EXPIRED) ⇒ 409 INVALID_STATE:
    // rebusca p/ o card mostrar o estado terminal em vez de falhar em silêncio.
    onError: () => {
      if (conversationId) void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
    meta: { skipErrorToast: true },
  });
}

// Gestão de conversas (histórico in-dock). Todas privadas do usuário (garantido no service).
export function useCreateConversation() {
  const qc = useQueryClient();
  const setActive = useAssistantDock((s) => s.setActiveConversation);
  return useMutation({
    mutationFn: (title?: string) =>
      api.post<ConversationSummary>("/api/v1/assistant/conversations", { title }),
    onSuccess: (conv) => {
      setActive(conv.id);
      void qc.invalidateQueries({ queryKey: conversationsKey });
    },
  });
}

export function useRenameConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; title: string }) =>
      api.patch<ConversationSummary>(`/api/v1/assistant/conversations/${v.id}`, { title: v.title }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: conversationsKey });
      void qc.invalidateQueries({ queryKey: conversationKey(v.id) });
    },
  });
}

export function useDeleteConversation() {
  const qc = useQueryClient();
  const activeId = useAssistantDock((s) => s.activeConversationId);
  const setActive = useAssistantDock((s) => s.setActiveConversation);
  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/v1/assistant/conversations/${id}`),
    onSuccess: (_r, id) => {
      if (activeId === id) setActive(null);
      void qc.invalidateQueries({ queryKey: conversationsKey });
      void qc.invalidateQueries({ queryKey: conversationKey(id) });
    },
  });
}
